/**
 * A teacher's display preferences for the gradebook.
 *
 * These existed as four checkboxes in Settings > Preferences that were local
 * component state: nothing saved them and nothing read them. They had looked like
 * settings since the day they were added.
 *
 * Fixing them meant deciding what each one could honestly do:
 *
 *  - SHOW PERCENTAGES already worked, just somewhere else. The gradebook has a
 *    live toggle for it stored per-browser in localStorage, so the Settings
 *    checkbox was a dead duplicate of a working control. It now backs the same
 *    preference on the teacher's profile, which also makes it follow them between
 *    devices instead of being forgotten on a different laptop.
 *  - COMPACT MODE and COLOUR-CODED GRADES were not implemented at all. They are
 *    now, because they are reasonable things to want and cheap to honour.
 *  - AUTO-SAVE IS GONE. Grades in this app deliberately do NOT auto-save — there
 *    is a "save my progress" action, and that is a designed behaviour a teacher
 *    relies on. A switch implying it could be turned on would have been worse than
 *    a switch that did nothing.
 *
 * Under `settings` for the same reason as the rest: `settings` is already on the
 * users-doc allowlist in firestore.rules, so no rules change and no deploy.
 */

export interface DisplayPrefs {
  /** Show and enter marks as percentages rather than raw scores. */
  showPercentage: boolean;
  /** Tint grade cells by achievement level. */
  colorCodeGrades: boolean;
  /** Tighter rows, so more students fit on screen. */
  compactMode: boolean;
}

export const DEFAULT_DISPLAY_PREFS: DisplayPrefs = {
  // Raw marks by default: a teacher enters 17 out of 20, not 85.
  showPercentage: false,
  // OFF, because that is what the gradebook's own colour toggle has always
  // defaulted to. A "better" default here would repaint every existing teacher's
  // grid the day this ships, which is not a preference, it is a surprise.
  colorCodeGrades: false,
  compactMode: false,
};

/** The browser key the percentage toggle used before it moved to the profile. */
export const LEGACY_PERCENTAGE_KEY = 'luc_showPercentage';

/**
 * Clean stored prefs.
 *
 * Every key is read independently and falls back to its default, so a doc written
 * by an older build — or half-written by a failed save — cannot turn the gradebook
 * into something unrecognisable.
 */
export function normalizeDisplayPrefs(raw: unknown): DisplayPrefs {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw))
    ? raw as Record<string, unknown> : {};
  const bool = (k: keyof DisplayPrefs) =>
    typeof src[k] === 'boolean' ? src[k] as boolean : DEFAULT_DISPLAY_PREFS[k];
  return {
    showPercentage: bool('showPercentage'),
    colorCodeGrades: bool('colorCodeGrades'),
    compactMode: bool('compactMode'),
  };
}

/**
 * The prefs to start from, migrating the old per-browser percentage choice.
 *
 * A teacher who set percentage mode in the gradebook should not find it reset the
 * first time this reads from their profile instead. The browser value is used only
 * when the profile has never stored one.
 */
export function initialDisplayPrefs(
  stored: unknown, legacyBrowserValue: string | null,
): DisplayPrefs {
  const prefs = normalizeDisplayPrefs(stored);
  const hasStored = !!stored && typeof stored === 'object'
    && Object.prototype.hasOwnProperty.call(stored, 'showPercentage');
  if (!hasStored && legacyBrowserValue !== null) {
    return { ...prefs, showPercentage: legacyBrowserValue === 'true' };
  }
  return prefs;
}

/** Did anything actually change? Used to skip a pointless write. */
export const displayPrefsEqual = (a: unknown, b: unknown): boolean => {
  const x = normalizeDisplayPrefs(a), y = normalizeDisplayPrefs(b);
  return x.showPercentage === y.showPercentage
    && x.colorCodeGrades === y.colorCodeGrades
    && x.compactMode === y.compactMode;
};
